/**
 * La canne a peche, dans la main du bonhomme.
 *
 * POURQUOI UN FICHIER A PART. Le bonhomme se reconstruit segment par segment
 * quand son apparence change, et la canne n'est pas une apparence: elle depend
 * de l'inventaire. On l'accroche donc au pivot `mainD`, qui survit aux
 * reconstructions (celles-ci ne touchent que le groupe de maillage de la main),
 * sans ecrire une ligne dans client/character/bonhomme.ts.
 *
 * Une seule geometrie fusionnee, un seul maillage, donc un seul appel de dessin,
 * et il n'existe que si le joueur possede vraiment une canne.
 */
import * as THREE from 'three'
import type { ArticulationsBonhomme } from '../character/bonhomme.ts'
import { fusionner, tr } from './geometrie.ts'

/** Longueur de la tige, en metres. Assez longue pour se lire de dessus a 45. */
const LONGUEUR = 1.15

const COULEUR_TIGE = 0xb98b4e
const COULEUR_POIGNEE = 0x5a3a22
const COULEUR_FIL = 0xf0efe4
const COULEUR_FLOTTEUR = 0xe23b3b

/**
 * Inclinaison de la canne dans la main, en radians.
 *
 * Le repere de la main a le meme axe que le torse, et le bonhomme regarde vers
 * +Z (`rot = atan2(ux, uz)`). On couche donc la tige vers l'avant en la
 * relevant: le scion sort devant le joueur, du cote de l'eau qu'il vise, et la
 * tige ne traverse ni la jambe ni le sol.
 */
const INCLINAISON = 1.2

/** Longueur du fil qui pend du scion, en metres. */
const LONGUEUR_FIL = 0.75

/**
 * Direction « vers le bas » vue depuis la canne inclinee: c'est la verticale du
 * monde ramenee dans le repere de la tige, au repos.
 */
const FIL_Y = -Math.cos(INCLINAISON)
const FIL_Z = Math.sin(INCLINAISON)

function tige(): THREE.BufferGeometry {
  // Conique: epaisse au talon, fine au scion. Six cotes suffisent a cette
  // taille, et c'est trois fois moins de triangles qu'un cylindre lisse.
  const g = new THREE.CylinderGeometry(0.006, 0.018, LONGUEUR, 6, 1, true)
  return g
}

export class CanneEnMain {
  private maille: THREE.Mesh | null = null
  private readonly materiau = new THREE.MeshLambertMaterial({ vertexColors: true })
  private readonly articulations: ArticulationsBonhomme

  constructor(articulations: ArticulationsBonhomme) {
    this.articulations = articulations
  }

  /** Montre ou cache la canne. Ne fabrique la geometrie qu'au premier besoin. */
  montrer(visible: boolean): void {
    if (!visible) {
      if (this.maille) this.maille.visible = false
      return
    }
    if (!this.maille) this.maille = this.batir()
    this.maille.visible = true
  }

  private batir(): THREE.Mesh {
    const demi = LONGUEUR / 2
    const geo = fusionner([
      { geo: tige(), couleur: COULEUR_TIGE, transforme: tr([0, demi, 0]) },
      {
        geo: new THREE.CylinderGeometry(0.022, 0.022, 0.16, 6),
        couleur: COULEUR_POIGNEE,
        transforme: tr([0, 0.05, 0]),
      },
      // Le moulinet: une pastille sur le cote, qui donne l'echelle et dit ce
      // que c'est d'un coup d'oeil, meme vu de loin.
      {
        geo: new THREE.CylinderGeometry(0.035, 0.035, 0.02, 8),
        couleur: COULEUR_POIGNEE,
        transforme: tr([0.035, 0.17, 0], [0, 0, Math.PI / 2]),
      },
      // Le fil, du scion vers le bas, et son flotteur rouge au bout. C'est lui
      // qui fait lire « peche » plutot que « baton »: sans fil, la silhouette
      // est celle d'une canne a marcher. Sa direction est calculee depuis
      // INCLINAISON pour qu'il pende vers le sol quel que soit l'angle choisi.
      {
        geo: new THREE.CylinderGeometry(0.004, 0.004, LONGUEUR_FIL, 4, 1, true),
        couleur: COULEUR_FIL,
        transforme: tr(
          [0, LONGUEUR + (FIL_Y * LONGUEUR_FIL) / 2, (FIL_Z * LONGUEUR_FIL) / 2],
          [Math.PI - INCLINAISON, 0, 0],
        ),
      },
      {
        geo: new THREE.SphereGeometry(0.03, 6, 5),
        couleur: COULEUR_FLOTTEUR,
        transforme: tr([0, LONGUEUR + FIL_Y * LONGUEUR_FIL, FIL_Z * LONGUEUR_FIL]),
      },
    ])
    const maille = new THREE.Mesh(geo, this.materiau)
    maille.name = 'canne'
    maille.rotation.x = INCLINAISON
    // Le pivot de la main est au poignet: on decale le talon dans la paume.
    maille.position.set(0, -0.04, 0.03)
    this.articulations.mainD.add(maille)
    return maille
  }

  dispose(): void {
    if (!this.maille) return
    this.maille.removeFromParent()
    this.maille.geometry.dispose()
    this.materiau.dispose()
    this.maille = null
  }
}
